import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

type Listener = ((event: Event) => void) | undefined;

/**
 * React 19 forwards `on*` props on custom elements to addEventListener using the
 * prop's own casing (`onNextPage` -> "NextPage"), but Polaris web components
 * dispatch lowercase events ("nextpage", "dismiss", "hide"). React's built-in
 * names (onClick, onChange, onInput, onFocus, onBlur) work as props; use this
 * ref for everything else.
 */
export function usePolarisEvents<E extends HTMLElement = HTMLElement>(handlers: Record<string, Listener>) {
    const latest = useRef(handlers);
    latest.current = handlers;
    const types = Object.keys(handlers).sort().join(' ');

    return useCallback((element: E | null) => {
        if (!element || !types) return;
        const listeners = types.split(' ').map(type => [type, (event: Event) => latest.current[type]?.(event)] as const);
        listeners.forEach(([type, listener]) => element.addEventListener(type, listener));
        return () => listeners.forEach(([type, listener]) => element.removeEventListener(type, listener));
    }, [types]);
}

type OverlayElement = HTMLElement & { showOverlay?: () => void; hideOverlay?: () => void };

/**
 * Drives an <s-modal> from React state. `onHide` runs when the merchant closes
 * the modal (Escape, backdrop, close button) so state can follow.
 */
export function useModalOpen(open: boolean, onHide: () => void, otherEvents: Record<string, Listener> = {}) {
    const element = useRef<OverlayElement | null>(null);
    const latestOpen = useRef(open);
    latestOpen.current = open;
    const events = usePolarisEvents<OverlayElement>({
        ...otherEvents,
        hide: () => {
            // Closed by state already (hideOverlay from the effect below).
            if (!latestOpen.current) return onHide();
            // Commit the state change now so the check below reads the new
            // `open`; otherwise a busy render queue re-shows a closed modal.
            flushSync(onHide);
            // The modal closes itself on Escape or its close button. When state
            // keeps it open (for example while saving), show it again.
            window.setTimeout(() => {
                if (latestOpen.current) element.current?.showOverlay?.();
            });
        },
    });
    const ref = useCallback((node: OverlayElement | null) => {
        element.current = node;
        return events(node);
    }, [events]);

    useEffect(() => {
        if (open) element.current?.showOverlay?.();
        else element.current?.hideOverlay?.();
    }, [open]);

    return ref;
}

/** Row selection for <s-table>, which has no built-in selection. */
export function useRowSelection(ids: string[]) {
    const [selected, setSelected] = useState<string[]>([]);
    // "All matching" extends a full-page selection to results on other pages.
    const [allMatching, setAllMatching] = useState(false);
    const idKey = ids.join('|');

    // Keep only rows that are still visible after the page or filters change.
    useEffect(() => {
        const visible = new Set(idKey ? idKey.split('|') : []);
        setSelected(current => {
            const next = current.filter(id => visible.has(id));
            return next.length === current.length ? current : next;
        });
        setAllMatching(false);
    }, [idKey]);

    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const allOnPage = ids.length > 0 && ids.every(id => selectedSet.has(id));

    const toggle = useCallback((id: string, checked: boolean) => {
        setAllMatching(false);
        setSelected(current => checked ? Array.from(new Set([...current, id])) : current.filter(value => value !== id));
    }, []);
    const togglePage = useCallback((checked: boolean) => {
        setAllMatching(false);
        setSelected(checked ? idKey.split('|').filter(Boolean) : []);
    }, [idKey]);
    const clear = useCallback(() => {
        setAllMatching(false);
        setSelected([]);
    }, []);

    return {
        selected,
        isSelected: (id: string) => selectedSet.has(id),
        allOnPage,
        someOnPage: selected.length > 0 && !allOnPage,
        allMatching,
        setAllMatching,
        toggle,
        togglePage,
        clear,
    };
}

/** Reads the current value of a Polaris form element from a change/input event. */
export const fieldValue = (event: Event | { currentTarget: EventTarget | null }) =>
    (event.currentTarget as HTMLInputElement | null)?.value ?? '';

/** Reads the checked state of an <s-checkbox> from a change event. */
export const fieldChecked = (event: Event | { currentTarget: EventTarget | null }) =>
    Boolean((event.currentTarget as HTMLInputElement | null)?.checked);

/** Reads the selected values of an <s-choice-list> from a change event. */
export const choiceValues = (event: Event | { currentTarget: EventTarget | null }) =>
    [...((event.currentTarget as (HTMLElement & { values?: string[] }) | null)?.values ?? [])];
